import { useEffect } from "react";

const GA_ID = import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined;
const CLARITY_ID = import.meta.env.VITE_CLARITY_PROJECT_ID as string | undefined;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: ((...args: unknown[]) => void) & { q?: unknown[] };
  }
}

function injectGA(id: string) {
  if (document.getElementById("ga-src")) return;
  const s = document.createElement("script");
  s.id = "ga-src";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  const gtag: (...args: unknown[]) => void = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", id, { send_page_view: true });
}

function injectClarity(id: string) {
  if (document.getElementById("clarity-src")) return;
  // Vendor snippet, adapted to TS + idempotent insertion.
  const w = window as Window;
  const c = w.clarity ||
    Object.assign(
      function (...args: unknown[]) {
        (c.q = c.q || []).push(args);
      } as ((...args: unknown[]) => void) & { q?: unknown[] },
      { q: [] as unknown[] },
    );
  w.clarity = c;
  const t = document.createElement("script");
  t.id = "clarity-src";
  t.async = true;
  t.src = `https://www.clarity.ms/tag/${id}`;
  const y = document.getElementsByTagName("script")[0];
  y.parentNode!.insertBefore(t, y);
}

export default function Analytics() {
  useEffect(() => {
    if (!GA_ID && !CLARITY_ID) return;

    const run = () => {
      if (GA_ID) injectGA(GA_ID);
      if (CLARITY_ID) injectClarity(CLARITY_ID);
    };

    const schedule = () => {
      const w = window as Window & {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
      };
      if (typeof w.requestIdleCallback === "function") {
        w.requestIdleCallback(run, { timeout: 2000 });
      } else {
        setTimeout(run, 1000);
      }
    };

    if (document.readyState === "complete") {
      schedule();
      return;
    }
    window.addEventListener("load", schedule, { once: true });
    return () => window.removeEventListener("load", schedule);
  }, []);

  return null;
}
